/** Grid-to-points fragment shader. */

#ifdef GL_EXT_draw_buffers
  #extension GL_EXT_draw_buffers : require
#endif

precision highp float;

#pragma glslify: Kernel2 = require(../kernel/kernel-2);
#pragma glslify: UP2 = require(../matrix/up-2);
#pragma glslify: SVD2 = require(../matrix/svd-2);

points_useSamples
grid_useSamples

#ifdef points_useReads_position
  points_useReads_position
#endif
#ifdef points_useReads_motion
  points_useReads_motion
#endif
#ifdef points_useReads_mass
  points_useReads_mass
#endif
#ifdef points_useReads_affine
  points_useReads_affine
#endif
#ifdef points_useReads_deform
  points_useReads_deform
#endif
#ifdef points_useReads_stress
  points_useReads_stress
#endif

#if defined(points_output_position) || defined(points_output_motion) || \
    defined(points_output_affine)
  grid_useReads_mass
#endif

#if defined(points_output_position) || defined(points_output_motion) || \
    defined(points_output_affine) || defined(points_output_deform)
  #ifdef points_splits
    uniform sampler2D points_states[points_splits];
  #else
    uniform sampler2D points_states;
  #endif

  uniform float points_stepNow;
  uniform vec4 points_stateShape;
#endif

#if defined(points_output_position) || defined(points_output_motion) || \
    defined(points_output_affine)
  #ifdef grid_splits
    uniform sampler2D grid_states[grid_splits];
  #else
    uniform sampler2D grid_states;
  #endif

  uniform float grid_stepNow;
  uniform vec4 grid_stateShape;
#endif

#if defined(points_output_mass)
  /** @todo Move to looking up material types? */
  uniform float mass;
#endif
#if defined(points_output_stress)
  /** @todo Move to looking up material types? */
  uniform float volume;
#endif
#if defined(points_output_motion)
  uniform float speedClamp;
#endif
#if defined(points_output_position) || defined(points_output_motion)
  /** Volume change threshold comparison. */
  uniform vec2 volumeSplit;
  /** ASFLIP motion adjustment. */
  uniform float motionAdjust;
  /** ASFLIP position adjustment range. */
  uniform vec2 positionAdjusts;
#endif
#if defined(points_output_deform) || defined(points_output_stress)
  /**
  * Mechanics parameters.
  *
  * @todo Move to individual particles looking up material types?
  */

  /** Bulk modulus. */
  uniform float bulkMod;
  /** Shear modulus. */
  uniform float shearMod;
#endif
#if defined(points_output_deform)
  /**
  * Plasticity parameters.
  *
  * @todo Move to individual particles looking up material types?
  */
  uniform float materialFriction;
#endif
#if defined(points_output_position)
  uniform float boundsClamp;
#endif
#if defined(points_output_position) || defined(points_output_motion) || \
    defined(points_output_affine) || defined(points_output_deform) || \
    defined(points_output_stress)
  uniform float spawnOnAny;
  uniform float spawnOnOut;
  uniform float spawnOnSlow;
  uniform float spawnOnCentre;
#endif
#if defined(points_output_motion) || defined(points_output_position)
  uniform vec3 wander;
#endif

uniform float dt;

uniform vec4 bounds;
uniform float chaos;

#ifdef rescale_mass
  uniform vec2 rescaleMass;
#else
  const float rescaledMass = 1.0;
#endif
#ifdef rescale_position
  uniform vec2 rescalePosition;
#else
  const float rescaledPosition = 1.0;
#endif
#ifdef rescale_motion
  uniform vec2 rescaleMotion;
#else
  const float rescaledMotion = 1.0;
#endif
#ifdef rescale_affine
  uniform vec2 rescaleAffine;
#else
  const float rescaledAffine = 1.0;
#endif
#ifdef rescale_deform
  uniform vec2 rescaleDeform;
#else
  const float rescaledDeform = 1.0;
#endif
#ifdef rescale_stress
  uniform vec2 rescaleStress;
#else
  const float rescaledStress = 1.0;
#endif

varying vec2 points_uv;

const vec2 v1 = vec2(1);
const vec2 v0 = v1*0.0;
const mat2 m1 = mat2(1);
const mat2 m0 = m1*0.0;
const vec4 mv0 = vec4(m0);
const vec4 mv1 = vec4(m1);
const float eps = 1e-6;

#pragma glslify: lt = require(glsl-conditionals/when_lt);
#pragma glslify: le = require(glsl-conditionals/when_le);
#pragma glslify: gt = require(glsl-conditionals/when_gt);
#pragma glslify: ge = require(glsl-conditionals/when_ge);
#pragma glslify: eq = require(glsl-conditionals/when_eq);
#pragma glslify: tau = require(glsl-constants/TAU);
#pragma glslify: map = require(glsl-map);
#pragma glslify: det = require(glsl-determinant);
#pragma glslify: transpose = require(glsl-transpose);

#pragma glslify: offsetUV = require(@epok.tech/gl-gpgpu/src/lookup/offset-uv);

#pragma glslify: trace = require(../matrix/trace);
#pragma glslify: outerProduct = require(../vector/outer-product);
#pragma glslify: area = require(../vector/area);
#pragma glslify: length2 = require(../vector/length-2);

#if defined(rescale_mass) || defined(rescale_position) || \
    defined(rescale_motion) || defined(rescale_affine) || \
    defined(rescale_deform) || defined(rescale_stress)
  #pragma glslify: exponent = require(../vector/exponent);
#endif

#if defined(points_output_position) || defined(points_output_motion) || \
    defined(points_output_affine)
  #pragma glslify: toKernel = require(../kernel/to-kernel);
  #pragma glslify: toKernelOffset = require(../kernel/to-offset);
  #pragma glslify: toKernelDistance = require(../kernel/to-distance);
#endif
#if defined(points_output_deform) || defined(points_output_stress)
  #pragma glslify: toSVD = require(../matrix/to-svd-2);
#endif
#if defined(points_output_position) || defined(points_output_motion)
  #pragma glslify: random = require(glsl-random);

  #pragma glslify: onSphere = require(../on-sphere);
#endif
#if defined(points_output_position)
  #pragma glslify: respawn = require(../spawn/noise-bounds);
#endif
#if defined(points_output_deform)
  #pragma glslify: projectDruckerPrager = require(../dynamic/drucker-prager);
#endif
#if defined(points_output_stress)
  #pragma glslify: neoHookeanElastic = require(../dynamic/neo-hookean-elastic);
#endif

void main() {
  #ifdef pReadPosition
  #elif defined(points_output_position)
    #define pReadPosition points_reads_position_position
  #elif defined(points_output_motion)
    #define pReadPosition points_reads_motion_position
  #elif defined(points_output_affine)
    #define pReadPosition points_reads_affine_position
  #endif

  #ifdef pReadMotion
  #elif defined(points_output_position)
    #define pReadMotion points_reads_position_motion
  #elif defined(points_output_motion)
    #define pReadMotion points_reads_motion_motion
  #endif

  #ifdef pReadMass
  #elif defined(points_output_position)
    #define pReadMass points_reads_position_mass
  #elif defined(points_output_motion)
    #define pReadMass points_reads_motion_mass
  #elif defined(points_output_mass)
    #define pReadMass points_reads_mass_mass
  #elif defined(points_output_affine)
    #define pReadMass points_reads_affine_mass
  #elif defined(points_output_deform)
    #define pReadMass points_reads_deform_mass
  #elif defined(points_output_stress)
    #define pReadMass points_reads_stress_mass
  #endif

  #ifdef pReadDeform
  #elif defined(points_output_deform)
    #define pReadDeform points_reads_deform_deform
  #endif

  #if defined(points_output_position) || defined(points_output_motion) || \
      defined(points_output_affine) || defined(points_output_deform)
    points_tapState(offsetUV(points_uv, points_stateShape.xy));
    // points_tapState(points_uv);
  #endif

  #if defined(points_output_position) || defined(points_output_motion) || \
      defined(points_output_affine)
    #ifdef rescale_position
      float rescaledPosition = exponent(rescalePosition);
    #endif
    #ifdef rescale_motion
      float rescaledMotion = exponent(rescaleMotion);
    #endif
    #ifdef rescale_affine
      float rescaledAffine = exponent(rescaleAffine);
    #endif

    vec2 pPosition = rescaledPosition*
      points_data[pReadPosition].points_channels_position;

    vec2 boundsSize = abs(bounds.zw-bounds.xy);
    float boundsSide = min(boundsSize.x, boundsSize.y);

    /** Read grid material properties on 3 nodes in each axis. */
    vec2 gMotionFull = v0;
    vec2 gMotionBase = v0;
    mat2 gAffine = m0;
    /** Seems the right range, by trial, versus `[0, grid_stateShape.xy-1]`. */
    vec2 cellAt = map(pPosition, bounds.xy, bounds.zw, v0, grid_stateShape.xy);
    Kernel2 kernel = toKernel(cellAt);
    /** Scale from bounds-space to grid-scale; motions differ from positions. */
    /**
     * @todo Verify if this is the real solution or just masking; seems to
     *   differ from just changing `volumeSplit` values, and offer:
     *   - Remove jitter at low numbers of particles.
     *   - Keep close to the reference `volumeSplit` values... reasons unknown.
     *   - Make some intuitive sense.
     */
    // Original.
    // vec2 scaleBoundsToGrid = grid_stateShape.xy;
    // Fix or mask?
    vec2 scaleBoundsToGrid = grid_stateShape.xy/boundsSize;
    // vec2 scaleBoundsToGrid = grid_stateShape.xy/boundsSide;
    // Seems to have the same effect as the fix or mask...
    // vec2 scaleBoundsToGrid = v0;

    for(int gx = 0; gx < kernelSize; ++gx) {
      for(int gy = 0; gy < kernelSize; ++gy) {
        vec2 offset = toKernelOffset(vec2(gx, gy));
        /** Range: `base` is `[0, grid_stateShape.xy-1.0]`, `st` is `[0, 1]`. */
        vec2 st = (kernel.base+offset)/(grid_stateShape.xy-1.0);
        float weight = kernel.weights[gx].x*kernel.weights[gy].y;

        grid_tapState(offsetUV(st, grid_stateShape.xy));
        // grid_tapState(st);

        vec2 cMotionFull = rescaledMotion*
          grid_data[grid_reads_mass_motionFull].grid_channels_motionFull;

        vec2 cMotionBase = rescaledMotion*
          grid_data[grid_reads_mass_motionBase].grid_channels_motionBase;

        vec2 wmf = weight*cMotionFull;
        vec2 wmb = weight*cMotionBase;

        gMotionFull += wmf;
        gMotionBase += wmb;

        vec2 cellDist = toKernelDistance(offset, cellAt, kernel.base);

        gAffine += rescaledAffine*4.0*weight*
          outerProduct(cMotionFull*scaleBoundsToGrid, cellDist);
      }
    }
  #endif

  #if defined(points_output_position) || defined(points_output_motion)
    /**
    * Mix grid and point velocities for ASFLIP, according to whether the
    * material is separating.
    * Mix full-grid-motion with particle-motion minus base-grid-motion - same
    * as full-grid-motion but without stress or other effects applied, leaving
    * only those effects when subtracted.
    *
    * @see [The ASFLIP paper](https://drive.google.com/open?id=1-3XumWRY5oFtWJdjrpzK9wjY19ukhJxQ&usp=drive_fs)
    * @see [Taichi PyASFLIP implementation mixing old nodal motion](https://github.com/nepluno/pyasflip/blob/master/pyasflip.py#L410-L415)
    */
    vec2 pMotion = rescaledMotion*
      points_data[pReadMotion].points_channels_motion;

    vec2 motionSplit = motionAdjust*(pMotion-gMotionBase);
    vec2 wandered = onSphere(map(random(points_uv*chaos), 0.0, 1.0, -tau, tau));
  #endif

  #if defined(points_output_motion)
    vec2 motionTo = gMotionFull+motionSplit;

    /** Avoid slow spots, wander motion. */
    float wanderOn = ge(wander.s, 0.0);
    float wanderSlow = 1.0-clamp(dot(motionTo, motionTo)*wander.s, 0.0, 1.0);

    motionTo += wanderOn*wanderSlow*wander.t*boundsSide*wandered;

    float speed = length(motionTo);
    float moving = gt(speed, eps);

    motionTo *= mix(1.0,
      mix(1.0, min(speed, speedClamp), moving)/mix(1.0, speed, moving),
      ge(speedClamp, 0.0));
  #endif

  #if defined(points_output_affine) || \
      defined(points_output_position) || defined(points_output_deform)
    mat2 affineTo = gAffine;
  #endif

  #if defined(points_output_deform) || defined(points_output_position)
    /**
     * @todo Deformation and stress should ideally be calculated at the start of
     *   the time-step not the end. Unsure if it matters much in practice, but
     *   the output is used to calculate the position (deformation calculates
     *   the volume which affects ASFLIP positional adjustment), it may be
     *   better to move the deformation update to a separate step at the start
     *   of each time-step.
     *
     * @see [Taichi PyASFLIP DruckerPrager plasticity, Neo-Hookean Elasticity](https://github.com/nepluno/pyasflip/blob/master/pyasflip.py#L324-L336)
     */

    #ifdef rescale_deform
      float rescaledDeform = exponent(rescaleDeform);
    #endif

    mat2 pDeform = rescaledDeform*
      mat2(points_data[pReadDeform].points_channels_deform);

    mat2 deformTo = (mat2(1)+(dt*affineTo))*pDeform;

    SVD2 deformSVD = toSVD(deformTo);

    // Plasticity flow.
    deformSVD.sigma = projectDruckerPrager(deformSVD.sigma,
      bulkMod, shearMod, materialFriction);

    // Reconstruct elastic deformation gradient after plasticity flow.
    deformTo = deformSVD.u*deformSVD.sigma*transpose(deformSVD.v);
  #endif

  #if defined(points_output_position)
    /**
     * @todo Try alternative volume ratio for materials where the stress depends
     *   only on the volume ratio (e.g: weakly-compressible liquid), with large
     *   timestep.
     * @see [Equation 6 in ASFLIP paper](https://drive.google.com/open?id=1-3XumWRY5oFtWJdjrpzK9wjY19ukhJxQ&usp=drive_fs)
     */
    float volumeRatio = det(deformTo);

    /**
     * @todo Check again if scaling `affine` is fixing or masking the grid
     *   artefact issue:
     *   - It seems to help reduce jitter with few points, as well as address
     *     the grid artefact issue; does this indicate it works, or just reduce
     *     another artefact with `deform` coincidentally?
     *   - It seems like the large-scale effect looks little-to-no different to
     *     setting `positionAdjusts[1]=0`; is it simply doing that, in effect?
     *   - The key difference in this part of the process is `stress` (and the
     *     `deform` it relies on): does the issue simply lie in how these are
     *     calculated, or in them being calculated at the step's end not start?
     */
    float dilation = log(max(volumeSplit.s, volumeRatio))+(trace(affineTo)*dt);
    float dilating = ge(dilation, volumeSplit.t);
    float positionAdjust = mix(positionAdjusts.x, positionAdjusts.y, dilating);
    vec2 positionSplit = positionAdjust*motionSplit;
    vec2 positionTo = pPosition+((gMotionFull+positionSplit)*dt);

    /** Like fractal Brownian motion to help any stuck clumps separate. */
    positionTo += wander.p*boundsSide*wandered;

    // Boundaries.
    positionTo = mix(positionTo,
      clamp(positionTo, bounds.xy, bounds.zw), boundsClamp);
  #endif

  /**
   * @todo This stress update is in grid-to-points just to avoid another
   *   `toSVD(pDeform)` in the points-to-grid step; reconsider this?
   */
  #if defined(points_output_stress)
    #ifdef rescale_stress
      float rescaledStress = exponent(rescaleStress);
    #endif

    /**
     * `D**-1` term for APIC transfers.
     *
     * @see [APIC transfer `D**-1` in paragraph after Eqn. 176](file:///Users/eoghanokeeffe/Library/CloudStorage/GoogleDrive-keeffeoghan@gmail.com/My%20Drive/Read/Fluid%20Continuum%20Physics/Material%20Point%20Method/The%20Material%20Point%20Method%20for%20Simulating%20Continuum%20Materials%20(mpm.graphics).pdf)
     *
     * @todo `D**-1` seems to be for points-to-grid transfers, not for
     *   grid-to-points; should this use the inverse in grid-to-points, or does
     *   it not matter, or does an issue arise from moving this stress step from
     *   points-to-grid to grid-to-points?
     */
    float dInv = 4.0*area(grid_stateShape.xy);
    // float dInv = 1.0/(4.0*area(grid_stateShape.xy));
    // float dInv = 1.0;

    /**
     * Neo-Hookean elasticity stress to Cauchy stress.
     *
     * @see [MLS-MPM-88](https://github.com/yuanming-hu/taichi_mpm/blob/master/mls-mpm88-explained.cpp#L100)
     * @see [Taichi PyASFLIP](https://github.com/nepluno/pyasflip/blob/master/pyasflip.py#L334)
     * @see [Incremental MPM](https://github.com/nialltl/incremental_mpm/blob/master/Assets/2.%20MLS_MPM_NeoHookean_Multithreaded/MLS_MPM_NeoHookean_Multithreaded.cs#L262)
     */
    // mat2 stressTo = -dt*volume*volumeRatio*dInv*
    mat2 stressTo = -dt*volume*dInv*
      neoHookeanElastic(deformSVD.u, deformSVD.sigma, bulkMod, shearMod);
  #endif

  /** @todo Better to move spawning into another pass for external control? */
  #if defined(points_output_position) || defined(points_output_motion) || \
      defined(points_output_mass) || defined(points_output_affine) || \
      defined(points_output_deform) || defined(points_output_stress)
    #ifdef rescale_mass
      float rescaledMass = exponent(rescaleMass);
    #endif

    float pMass = rescaledMass*points_data[pReadMass].points_channels_mass;

    vec2 spawnOut = spawnOnOut*
      (lt(positionTo, bounds.xy)+lt(bounds.zw, positionTo));

    // Avoid slow spots, respawn.
    float spawnSlow = spawnOnSlow*lt(dot(motionTo, motionTo), spawnOnSlow);

    // Avoid dead centre.
    float spawnCentre = gt(spawnOnCentre, 0.0)*
      lt(length2(positionTo/boundsSide), spawnOnCentre*spawnOnCentre);

    float spawn =
      spawnOnAny*gt(spawnOut.x+spawnOut.y+spawnSlow+spawnCentre, 0.0);
  #endif
  #if defined(points_output_position) || defined(points_output_motion)
    float hold = le(pMass, 0.0);
    float spawnOrHold = max(spawn, hold);
  #endif
  #if defined(points_output_mass) || defined(points_output_affine) || \
      defined(points_output_deform) || defined(points_output_stress)
    float empty = eq(pMass, 0.0);
  #endif
  #if defined(points_output_affine) || \
      defined(points_output_deform) || defined(points_output_stress)
    float spawnOrEmpty = max(spawn, empty);
  #endif

  #ifdef points_output_position
    points_output_position = mix(positionTo,
        respawn(points_uv, bounds.xy, bounds.zw, 1.0/points_stateShape.xy),
        spawnOrHold)/
      rescaledPosition;
  #endif
  #ifdef points_output_motion
    points_output_motion =
      mix(motionTo, v0, spawnOrHold)/rescaledMotion;
  #endif
  #ifdef points_output_mass
    points_output_mass = mix(pMass, mass, empty)/rescaledMass;
  #endif
  #ifdef points_output_affine
    points_output_affine =
      mix(vec4(affineTo), mv0, spawnOrEmpty)/rescaledAffine;
  #endif
  #ifdef points_output_deform
    points_output_deform =
      mix(vec4(deformTo), mv1, spawnOrEmpty)/rescaledDeform;
  #endif
  #ifdef points_output_stress
    points_output_stress =
      mix(vec4(stressTo), mv1, spawnOrEmpty)/rescaledStress;
  #endif
}
